import { Component, Prop, h, State, Watch, Element, Method } from '@stencil/core';

@Component({
  tag: 'theme-provider',
  shadow: true,
})
export class ThemeProvider {
  @Element() hostElement: HTMLElement;
  
  /**
   * Path to the CSS theme file
   */
  @Prop() themePath: string;
  
  /**
   * Current theme mode
   */
  @Prop({ mutable: true, reflect: true }) mode: 'light' | 'dark' = 'light';
  
  /**
   * Internal stylesheet element reference
   */
  @State() styleElement: HTMLLinkElement | null = null;
  
  /**
   * Watch for changes to the theme path and update the stylesheet
   */
  @Watch('themePath')
  handleThemePathChange(newPath: string) {
    if (newPath) {
      this.loadThemeFromPath(newPath);
    }
  }
  
  /**
   * Watch for changes to theme mode
   */
  @Watch('mode')
  handleModeChange(newMode: 'light' | 'dark') {
    this.updateThemeClass(newMode);
  }
  
  /**
   * Component lifecycle method that runs once after the component is first connected to the DOM
   */
  componentDidLoad() {
    // Load theme from path if provided
    if (this.themePath) {
      this.loadThemeFromPath(this.themePath);
    }
    
    // Set initial theme class
    this.updateThemeClass(this.mode);
  }
  
  /**
   * Update the theme class on the document body
   */
  private updateThemeClass(mode: 'light' | 'dark') {
    // Remove existing theme classes
    document.documentElement.classList.remove('theme-light', 'theme-dark');
    
    // Add the appropriate class
    document.documentElement.classList.add(`theme-${mode}`);
    
    // Dispatch a theme change event
    const themeChangeEvent = new CustomEvent('themechange', {
      detail: { mode },
      bubbles: true,
      composed: true
    });
    this.hostElement.dispatchEvent(themeChangeEvent);
  }
  
  /**
   * Load a theme from a CSS file path
   */
  private loadThemeFromPath(path: string) {
    // Remove any existing stylesheet if it exists
    if (this.styleElement) {
      document.head.removeChild(this.styleElement);
    }
    
    // Create a new link element for the CSS file
    this.styleElement = document.createElement('link');
    this.styleElement.rel = 'stylesheet';
    this.styleElement.href = path;
    
    // Add an ID to make it easier to find and remove later if needed
    this.styleElement.id = 'isometrik-theme-stylesheet';
    
    // Append the stylesheet to the document head
    document.head.appendChild(this.styleElement);
  }
  
  /**
   * Public method to toggle between light and dark themes
   */
  @Method()
  async toggleTheme() {
    this.mode = this.mode === 'light' ? 'dark' : 'light';
    return this.mode;
  }
  
  /**
   * Public method to set theme explicitly
   */
  @Method()
  async setTheme(mode: 'light' | 'dark') {
    this.mode = mode;
    return this.mode;
  }
  
  render() {
    return (
      <div class={`theme-provider-container theme-${this.mode}`} style={{flex: '1', width: '100%'}}>
        <slot></slot>
      </div>
    );
  }
}